import { Icon, Stack, Tag, Text } from '@eluve/components';

import {
  type Offering,
  type Org,
  TYPE_META,
  categoryName,
} from './care-library-data';
import { OfferingMark, OfferingTypeLabel } from './offering-types';

/** Type-aware parameter line. Exercise keeps the mono dosage chip; every other
 *  type gets a plain clock-prefixed metadata line — never a forced sets×reps. */
const OfferingMeta: React.FC<{ offering: Offering }> = ({ offering }) => {
  if (offering.type === 'exercise') {
    return (
      <span className="inline-flex h-6 w-fit items-center rounded-md bg-bg-secondary px-2 font-mono text-xs font-medium text-text-secondary">
        {offering.meta}
      </span>
    );
  }
  return (
    <Stack direction="horizontal" gap={1.5} align="center" fullWidth={false}>
      <Icon name="Clock" size="xs" className="text-text-supporting" />
      <Text inline size="xs" color="secondary">
        {offering.meta}
      </Text>
    </Stack>
  );
};

/**
 * A single catalog card. Shows the type mark + eyebrow, the offering name, a
 * type-appropriate parameter line, a clamped description, the primary +
 * secondary category chips, and a "used in N plans" badge.
 */
export const OfferingCard: React.FC<{
  offering: Offering;
  org: Org;
  onClick?: () => void;
}> = ({ offering, org, onClick }) => {
  const primaryFacet = org.facets.find((f) => f.primary);
  const secondFacet = org.facets.find(
    (f) => !f.primary && (offering.cats[f.id] ?? []).length > 0,
  );

  const chips: string[] = [];
  if (primaryFacet) {
    (offering.cats[primaryFacet.id] ?? [])
      .slice(0, 1)
      .forEach((cid) => chips.push(categoryName(org, primaryFacet.id, cid)));
  }
  if (secondFacet) {
    (offering.cats[secondFacet.id] ?? [])
      .slice(0, 1)
      .forEach((cid) => chips.push(categoryName(org, secondFacet.id, cid)));
  }
  const totalCats = Object.values(offering.cats).reduce(
    (n, ids) => n + ids.length,
    0,
  );
  const extra = totalCats - chips.length;

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex flex-col gap-2.5 rounded-xl border border-border-primary bg-bg-primary p-4 text-left shadow-xs transition-colors hover:border-border-secondary"
    >
      <Stack direction="horizontal" gap={3} align="start" fullWidth>
        <OfferingMark type={offering.type} />
        <div className="min-w-0 flex-1">
          <OfferingTypeLabel type={offering.type} />
          <Text size="sm" weight="semibold" className="mt-0.5 leading-tight">
            {offering.name}
          </Text>
        </div>
      </Stack>

      <OfferingMeta offering={offering} />

      <Text size="xs" color="tertiary" className="line-clamp-2 leading-snug">
        {offering.desc}
      </Text>

      <Stack direction="horizontal" gap={1.5} align="center" wrap fullWidth>
        {chips.map((chip) => (
          <Tag key={chip} size="xs" color="gray" label={chip} />
        ))}
        {extra > 0 && (
          <Text inline size="xs" color="supporting" className="font-mono">
            +{extra}
          </Text>
        )}
      </Stack>
    </button>
  );
};

/** Re-export for callers building their own headers. */
export { TYPE_META };
